import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import formatPrice from "@/core/helpers/formatPrice";
import { imgDots } from "@/shared/assets/img";
import { BoxIcon, CartIcon, Info2Icon } from "@/shared/icons";
import { Tooltip } from "@/shared/ui";
import Button from "@/shared/ui/button/Button";
import GoodsAndWarehouseTooltip from "@/domains/business/components/goodsAndWarehouse/GoodsAndWarehouseTooltip";

type Props = {
  id: number;
  title: string;
  description: string;
  price: number;
  currentCount: number;
  src: string;
  onCountChange: (id: number, count: number) => void;
  initialCount?: number;
  containerBounds: { left: number; right: number };
};

export default function GoodsAndWarehouseCard({
  id,
  title,
  description,
  price,
  currentCount,
  src,
  onCountChange,
  initialCount = 0,
  containerBounds,
}: Props) {
  const { t } = useTranslation("business");
  const [tooltipSide, setTooltipSide] = useState<"left" | "right">("right");
  const iconRef = useRef<HTMLDivElement | null>(null);

  const handleIncrement = useCallback(() => {
    const newCount = initialCount + 1;
    onCountChange(id, newCount);
  }, [id, initialCount, onCountChange]);

  const handleDecrement = useCallback(() => {
    const newCount = initialCount - 1;
    onCountChange(id, newCount);
  }, [id, initialCount, onCountChange]);

  const handleAddToCart = useCallback(() => {
    const newCount = 1;
    onCountChange(id, newCount);
  }, [id, onCountChange]);

  const handleMouseEnter = () => {
    if (iconRef.current) {
      const iconRect = iconRef.current.getBoundingClientRect();
      const spaceRight = containerBounds.right - iconRect.right;
      const spaceLeft = iconRect.left - containerBounds.left;
      const tooltipWidth = 220;

      if (spaceRight >= tooltipWidth + 8) {
        setTooltipSide("right");
      } else if (spaceLeft >= tooltipWidth + 8) {
        setTooltipSide("left");
      } else {
        setTooltipSide("right");
      }
    }
  };

  const tooltipContent = <GoodsAndWarehouseTooltip title={title} description={description} />;

  return (
    <div className="group flex flex-col p-5 bg-dark-800 rounded-[0.75rem]">
      <div
        className="relative flex flex-wrap w-full h-[9.375rem]  bg-center bg-no-repeat"
        style={{
          backgroundImage: `url(${imgDots})`,
        }}
      >
        <div
          className="absolute z-1 top-1/2 left-1/2 size-full -translate-x-1/2 -translate-y-1/2"
          style={{
            backgroundImage: `url(${src})`,
            backgroundSize: "contain",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
          }}
        />
        <div
          ref={iconRef}
          onMouseEnter={handleMouseEnter}
          className="relative z-3 size-4.5 ml-auto mb-auto opacity-30 cursor-pointer hover:opacity-100"
        >
          <Tooltip content={tooltipContent} position={tooltipSide} tooltipClassName="-top-7">
            <Info2Icon className="size-4.5" />
          </Tooltip>
        </div>
      </div>
      <p className="mb-1 text-16 font-medium">{title}</p>
      <p className="mb-5 text-16 text-accent-solid font-bold">{formatPrice(price)}₽</p>
      <div className="flex items-center mb-3.5">
        <BoxIcon className="size-3.5 text-white/30" />
        <p className="ml-1 text-14 font-medium text-white/60 leading-none">
          {t("business.goodsAndWarehouse.inWarehouse")}:
        </p>
        <p className="ml-auto text-12 font-bold leading-none">
          {currentCount} {t("business.goodsAndWarehouse.cnt")}.
        </p>
      </div>

      {initialCount > 0 ? (
        <div className="flex w-full h-10.5 p-1 bg-dark-900 rounded-xl">
          <Button
            className="relative size-8.5 rounded-xl"
            icon
            onClick={handleDecrement}
            variant="soft"
            color="gray"
          >
            <span className="absolute size-full top-0 left-0 bg-gradient-accent rounded-xl opacity-0 transition-all ease-in-out duration-200 hover:opacity-100" />
            <span className="relative z-1 w-2.5 h-0.5 bg-white pointer-events-none" />
          </Button>
          <p className="flex justify-center items-center grow text-14 font-bold">{initialCount}</p>
          <Button
            className="group relative size-8.5 rounded-xl"
            icon
            onClick={handleIncrement}
            variant="soft"
            color="gray"
          >
            <span className="absolute size-full top-0 left-0 bg-gradient-accent rounded-xl opacity-0 transition-all ease-in-out duration-200 hover:opacity-100" />
            <span className="relative z-1 w-2.5 h-0.5 bg-white pointer-events-none">
              <span className="absolute top-1/2 left-1/2 w-0.5 h-2.5 bg-white -translate-x-1/2 -translate-y-1/2" />
            </span>
          </Button>
        </div>
      ) : (
        <Button
          onClick={handleAddToCart}
          className="w-full h-10.5 font-bold rounded-xl bg-accent-muted/20 hover:brightness-110"
          variant="solid"
        >
          <div className="flex items-center justify-center gap-2.5">
            <CartIcon className="size-4.5 text-accent-solid" />
            <p className="leading-none">{t("business.goodsAndWarehouse.inCart")}</p>
          </div>
        </Button>
      )}
    </div>
  );
}
